import React, { useState } from 'react';
import { Button } from '@resource-kit/ui/react';
import PasswordField from './PasswordField';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import { validatePassword } from '@lib/password';
import { useTranslation } from 'react-i18next';

interface PasswordChangeFormProps {
  onPasswordChange: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<{ success: boolean; error?: unknown }>;
  loading?: boolean;
}

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  onPasswordChange,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showForm, setShowForm] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = t('passwordChange.currentRequired');
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.valid) {
      newErrors.newPassword = passwordValidation.message || t('passwordChange.passwordInvalid');
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('passwordChange.passwordMismatch');
    }

    if (formData.currentPassword && formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = t('passwordChange.passwordSame');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await onPasswordChange(formData.currentPassword, formData.newPassword);

      if (result.success) {
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setShowForm(false);
      }
    } catch (error) {
      console.error('Password change error:', error);
    }
  };

  const handleCancel = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
    setShowForm(false);
  };

  if (!showForm) {
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-foreground">{t('passwordChange.password')}</h3>
            <p className="text-sm text-muted-foreground">{t('passwordChange.passwordDesc')}</p>
          </div>
          <Button onClick={() => setShowForm(true)} variant="outline">
            {t('passwordChange.changePassword')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium text-foreground">{t('passwordChange.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('passwordChange.subtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <PasswordField
          name="currentPassword"
          label={t('passwordChange.currentPassword')}
          value={formData.currentPassword}
          onChange={(e) => handleInputChange('currentPassword', e.target.value)}
          error={errors.currentPassword}
          autoComplete="current-password"
          required
        />

        <PasswordField
          name="newPassword"
          label={t('passwordChange.newPassword')}
          value={formData.newPassword}
          onChange={(e) => handleInputChange('newPassword', e.target.value)}
          error={errors.newPassword}
          autoComplete="new-password"
          required
        />

        {formData.newPassword && <PasswordStrengthMeter password={formData.newPassword} />}

        <PasswordField
          name="confirmPassword"
          label={t('passwordChange.confirmNewPassword')}
          value={formData.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
          error={errors.confirmPassword}
          autoComplete="new-password"
          required
        />

        <div className="flex space-x-3 pt-4">
          <Button type="submit" disabled={loading}>
            {loading ? t('passwordChange.updating') : t('passwordChange.updatePassword')}
          </Button>
          <Button type="button" variant="outline" onClick={handleCancel} disabled={loading}>
            {t('ui.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PasswordChangeForm;
